import React from 'react';
import I18n from 'i18n-js';
import { ChildProps } from 'react-apollo';

import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { SearchSellFormLiveListingsTable } from '@reverbdotcom/commons/src/rql';

import { queryVariables, LIVE_LISTINGS_QUERY_DEFINITION } from '../SellFormPricingModal/SellFormLiveListingsModalContainer';
import { PAGE_SIZE } from '../SellFormPricingModal/SellFormPricingModal';
import PriceRecordsTable from './PriceRecordsTable';
import PriceRecordsEmptyState from './PriceRecordsEmptyState';

interface IExternalProps {
  canonicalProductIds: string[];
}

type IProps = ChildProps<IExternalProps, SearchSellFormLiveListingsTable.Query>;

export function convertListingsToPriceRecords(listings) {
  return listings.filter(listing => !!listing.pricing?.buyerPrice?.display && !!listing.publishedAt)
    .map(listing => ({
      timestamp: listing.publishedAt,
      conditionDisplayName: listing.condition.displayName,
      priceDisplay: listing.pricing?.buyerPrice?.display,
      id: listing._id,
    }));
}

export function LiveListingsTableContainer({
  canonicalProductIds,
  data,
}: IProps) {
  const {
    loading,
    listingsSearch = {},
    refetch,
  } = data ?? {};

  const {
    listings = [],
    total = 0,
    offset = 0,
  } = listingsSearch;

  const filteredPriceRecords = convertListingsToPriceRecords(listings);

  const showEmptyState = !loading && total === 0;
  const currentPage = offset / PAGE_SIZE;
  const totalPages = Math.floor(total / PAGE_SIZE);

  function onPageChange(pageChange: number) {
    return () => {
      const newPage = currentPage + pageChange;
      const newOffset = newPage * PAGE_SIZE;

      refetch({
        canonicalProductIds,
        offset: newOffset,
      });
    };
  }

  if (showEmptyState) {
    return (
      <PriceRecordsEmptyState
        text={I18n.t('discovery.sellForm.pricingSection.pricingGuidance.noLiveListings')}
      />
    );
  }

  return (
    <PriceRecordsTable
      priceRecords={filteredPriceRecords}
      onPageChange={onPageChange}
      currentPage={currentPage}
      totalPages={totalPages}
      dateHeader={I18n.t('discovery.sellForm.pricingSection.pricingTables.liveListings.dateHeader')}
      priceHeader={I18n.t('discovery.sellForm.pricingSection.pricingTables.liveListings.priceHeader')}
    />
  );
}

const withQuery = withGraphql<IProps, SearchSellFormLiveListingsTable.Query>(
  LIVE_LISTINGS_QUERY_DEFINITION,
  {
    skip: ({ canonicalProductIds }) => !canonicalProductIds?.length,
    options: ({
      canonicalProductIds,
    }) => {
      return {
        ssr: false,
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
        variables: queryVariables({
          canonicalProductIds,
        }),
      };
    },
  },
);
export default withQuery(LiveListingsTableContainer);
